// Import
import Tippy from '@tippyjs/react';
import { DateTime } from 'luxon';
import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components/macro';
import { Params, useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import tippyProps from '../../../../../../shareables/foundation/front-end/utils/tippy-props';
import apiActions from '../../../../../../shareables/integrations/api/redux/actions';
import { GetNewsArticleResponse } from '../../../../../../shareables/types/api/main/website/page/type/204/news-article/get';
import parseAndSanitizeMarkdown from '../../../../../../shareables/utils/markdown/parse-and-sanitize';
import Attachments from '../shared-components/attachments';
import { NewsArticleContent, NewsArticleTitle } from './news-articles';
import useScrollToElementOnLoad from '../../../../use-scroll-to-element-on-load';


// Styled components
const AttachmentsWrapper = styled.div`
	margin-top: 0.75rem;
`;


// Define the accepted props
interface Props {
	pageID: number;
	storybookNewsArticle?: GetNewsArticleResponse;
}


// Function component
const NewsArticle: React.FC<Props> = ({ pageID, storybookNewsArticle }) => {
	// Use state
	const [newsArticle, setNewsArticle] = useState<GetNewsArticleResponse | null>(storybookNewsArticle ?? null);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Use React Router functionality
	const { articleIdentifier } = useParams<Params>();
	
	
	// Initialize
	const articleID = articleIdentifier?.split('-').pop();
	
	
	// Use ref
	const titleRef = useRef<HTMLHeadingElement>(null);
	
	
	// Scroll to the article title once it loads, skipping back/forward navigation
	useScrollToElementOnLoad(titleRef, newsArticle);
	
	
	// Get news article on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetNewsArticleResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/page/id_${pageID}/type/204/news-article/${articleID ?? ''}`,
				completion: (json) => {
					setNewsArticle(json.response);
				},
			})
		);
	}, [dispatch, pageID, articleID]);
	
	
	// If loading, display nothing
	if (newsArticle === null) {
		return null;
	}
	
	
	// Return JSX
	return (
		<React.Fragment>
			<Helmet title={`${newsArticle.title} | ${document.title}`} />
			
			<NewsArticleTitle ref={titleRef}>
				<span>{newsArticle.title}</span>
				<small className='is-gray'>
					Published{' '}
					<Tippy
						{...tippyProps}
						content={new Date(newsArticle.published).toLocaleDateString(undefined, {
							month: 'long',
							day: 'numeric',
							year: 'numeric',
							hour: 'numeric',
							minute: '2-digit',
						})}
					>
						<span>{DateTime.fromISO(newsArticle.published).toRelative()}</span>
					</Tippy>
				</small>
			</NewsArticleTitle>
			
			<NewsArticleContent
				dangerouslySetInnerHTML={{
					__html: parseAndSanitizeMarkdown(newsArticle.text),
				}}
			/>
			
			{newsArticle.attachments.length > 0 && (
				<AttachmentsWrapper>
					<Attachments
						id={newsArticle.id}
						attachmentURIPrefix='article'
						attachments={newsArticle.attachments.map((attachment) => ({ ...attachment, name: attachment.title }))}
					/>
				</AttachmentsWrapper>
			)}
		</React.Fragment>
	);
};

export default NewsArticle;
